import React from 'react';
import { alpha } from '@mui/material';
import { IconButton } from '../../Actions/IconButton/IconButton';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useTheme } from '../../../theme/ThemeProvider';
import type { ThemeMode, AccessibilityEventData } from '../../../constants/interfaces/themeSettings';

export interface ThemeToggleProps {
  /** Edge size of the square tile in pixels */
  size?: number;
  /** Border radius of the tile in pixels */
  radius?: number;
  /** Called after theme is toggled with the next mode */
  onToggle?: (nextMode: Extract<ThemeMode, 'light' | 'dark'>) => void;
  /** Accessible label overrides */
  ariaLabelLight?: string;
  ariaLabelDark?: string;
  /** Disable the control */
  disabled?: boolean;
  /** Event callback for tracking user interactions */
  onEvent?: (data: AccessibilityEventData) => void;
}

/**
 * A compact square button that toggles between light and dark themes.
 * Visual style matches a soft, rounded tile with an 8-point star and crescent glyph.
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  size = 40,
  radius = 10,
  onToggle,
  ariaLabelLight = 'Switch to light theme',
  ariaLabelDark = 'Switch to dark theme',
  disabled = false,
  onEvent,
}) => {
  const themeCtx = useTheme();
  const isDark = themeCtx.resolvedMode === 'dark';

  const tileBg = isDark ? '#1f1f1f' : '#ffffff';
  const tileBorder = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';
  const tileInset = isDark ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.04)';
  const iconColor = isDark ? '#ffffff' : '#1f1f1f';

  const handleClick = () => {
    const next: Extract<ThemeMode, 'light' | 'dark'> = isDark ? 'light' : 'dark';
    
    // Log the theme toggle event
    onEvent?.({
      type: 'theme_toggle',
      value: next,
      previousMode: isDark ? 'dark' : 'light'
    });
    
    themeCtx.setTheme(next);
    if (onToggle) onToggle(next);
  };

  return (
    <IconButton
      onClick={handleClick}
      disabled={disabled}
      aria-label={isDark ? ariaLabelLight : ariaLabelDark}
      sx={(muiTheme) => ({
        width: size,
        height: size,
        borderRadius: `${radius}px`,
        backgroundColor: tileBg,
        border: `1px solid ${tileBorder}`,
        boxShadow: isDark
          ? `inset 0 1px 1px ${alpha('#000', 0.6)}, 0 1px 2px ${alpha('#000', 0.4)}`
          : `inset 0 1px 1px ${tileInset}, 0 1px 2px ${alpha('#000', 0.08)}`,
        transition: 'transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: isDark
            ? `inset 0 1px 1px ${alpha('#000', 0.7)}, 0 4px 10px ${alpha('#000', 0.5)}`
            : `inset 0 1px 1px ${alpha('#000', 0.06)}, 0 4px 10px ${alpha('#000', 0.12)}`,
          backgroundColor: isDark ? '#222' : '#fafafa',
        },
        '&:active': {
          transform: 'translateY(0)',
          boxShadow: isDark
            ? `inset 0 2px 3px ${alpha('#000', 0.75)}`
            : `inset 0 2px 3px ${alpha('#000', 0.1)}`,
        },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: `${isDark ? '0 0 0 3px rgba(255,255,255,0.2)' : '0 0 0 3px rgba(0,0,0,0.12)'} , 0 0 0 1px ${muiTheme.palette.primary.main}`,
        },
      })}
    >
      {!isDark ? (
        <Brightness4Icon sx={{ color: iconColor, fontSize: `${size * 0.6}px` }} />
      ) : (
        <Brightness7Icon sx={{ color: iconColor, fontSize: `${size * 0.6}px` }} />
      )}
    </IconButton>
  );
};

export default ThemeToggle;


